import {
	CHAT_BLOCK_SCHEMA_VERSION,
	type ChatBlock,
	type IChartBlock,
	type ICitationSource,
	type IMetricTile,
} from "./chat-block.types";

// Runtime validation for blocks arriving over the wire. The producer
// (reconciliation-ai) is trusted but not infallible: a drifted shape, an
// unknown block type, or a future schema version must degrade gracefully to an
// UnsupportedBlock instead of crashing a renderer (e.g. Recharts on a NaN, or
// AntD Table on a non-array). Valid blocks are capped and coerced so one large
// or malformed payload can't bloat a frame or blow up the DOM.
//
// This is deliberately hand-rolled rather than zod — equility-web has no zod
// dependency, and the block union is small and closed. See ./README.md for the
// contract these checks enforce.

// Display-side guards. The emitter already caps rows server-side; these are a
// second line of defense against a producer that ignores the contract.
const MAX_TABLE_ROWS = 50;
const MAX_CHART_POINTS = 60;

const CHART_VARIANTS: ReadonlySet<IChartBlock["variant"]> = new Set([
	"bar",
	"line",
	"area",
	"waterfall",
]);

function isObject(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function isString(value: unknown): value is string {
	return typeof value === "string";
}

// An in-app drill route must be a non-empty client path (starts with "/"). This
// is what reaches navigate() in CitationBlock/TableBlock, so anything else
// (external URLs, "javascript:", empty) is rejected before it can be followed.
function isSafeRoute(value: unknown): value is string {
	return isString(value) && value.startsWith("/");
}

// Monotonic suffix for blocks that arrive without an id. Blocks stream in one at
// a time, so a per-type constant ("table-0") collides as React keys when several
// id-less blocks of the same type arrive in one message; a running counter keeps
// the fallback id unique across the session.
let fallbackIdCounter = 0;

// Unknown/drifted blocks render via BlockRenderer's default case as an
// UnsupportedBlock, which reads `type`. "unsupported" is not in the ChatBlock
// union (it is a render-only fallback), so the cast is intentional.
function unsupported(id: string): ChatBlock {
	return { id, type: "unsupported" } as unknown as ChatBlock;
}

// Replace a non-finite number (NaN, ±Infinity) with 0 so charts never choke.
function finiteOrZero(value: unknown): number {
	return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

/**
 * Validate and normalize a raw block from the stream into a renderable
 * `ChatBlock`. Never throws; on any drift it returns an UnsupportedBlock rather
 * than dropping the block, so the user still sees that something arrived.
 */
export function safeParseBlock(raw: unknown): ChatBlock {
	if (!isObject(raw) || !isString(raw.type)) {
		return unsupported(
			isObject(raw) && isString(raw.id) ? raw.id : "block-invalid",
		);
	}

	const id = isString(raw.id)
		? raw.id
		: `${raw.type}-fallback-${fallbackIdCounter++}`;

	// A block stamped with a schema version this client doesn't understand is
	// treated as drift — better an honest "can't show" than a mis-render.
	if (
		raw.schemaVersion !== undefined &&
		raw.schemaVersion !== CHAT_BLOCK_SCHEMA_VERSION
	) {
		return unsupported(id);
	}

	switch (raw.type) {
		case "text": {
			if (!isString(raw.markdown)) return unsupported(id);
			return { id, type: "text", markdown: raw.markdown };
		}

		case "metric": {
			if (!Array.isArray(raw.tiles)) return unsupported(id);
			const tiles = raw.tiles
				.filter(
					(t): t is Record<string, unknown> =>
						isObject(t) && isString(t.label) && isString(t.value),
				)
				.slice(0, 3)
				.map((t): IMetricTile => {
					const tone =
						t.tone === "up" ||
						t.tone === "down" ||
						t.tone === "warn" ||
						t.tone === "neutral"
							? t.tone
							: undefined;
					return {
						label: t.label as string,
						value: t.value as string,
						...(isString(t.delta) ? { delta: t.delta } : {}),
						...(tone ? { tone } : {}),
					};
				});
			if (tiles.length === 0) return unsupported(id);
			return { id, type: "metric", tiles };
		}

		case "table": {
			if (!Array.isArray(raw.columns) || !Array.isArray(raw.rows)) {
				return unsupported(id);
			}
			const columns = raw.columns.filter(
				(c): c is Record<string, unknown> =>
					isObject(c) && isString(c.key) && isString(c.label),
			);
			if (columns.length === 0) return unsupported(id);
			const rows = raw.rows.filter(isObject).slice(0, MAX_TABLE_ROWS) as Record<
				string,
				unknown
			>[];
			const totalRows =
				typeof raw.totalRows === "number" && Number.isFinite(raw.totalRows)
					? raw.totalRows
					: rows.length;
			return {
				id,
				type: "table",
				...(isString(raw.title) ? { title: raw.title } : {}),
				columns: columns.map((c) => ({
					key: c.key as string,
					label: c.label as string,
					...(c.align === "left" || c.align === "right"
						? { align: c.align }
						: {}),
					...(c.kind === "money" ||
					c.kind === "date" ||
					c.kind === "text" ||
					c.kind === "status"
						? { kind: c.kind }
						: {}),
				})),
				rows,
				totalRows,
				...(isObject(raw.drilldown) && isSafeRoute(raw.drilldown.route)
					? { drilldown: { route: raw.drilldown.route } }
					: {}),
			};
		}

		case "chart": {
			if (!CHART_VARIANTS.has(raw.variant as IChartBlock["variant"])) {
				return unsupported(id);
			}
			if (!Array.isArray(raw.series)) return unsupported(id);
			const series = raw.series
				.filter(
					(p): p is Record<string, unknown> => isObject(p) && isString(p.label),
				)
				.slice(0, MAX_CHART_POINTS)
				.map((p) => ({
					label: p.label as string,
					value: finiteOrZero(p.value),
				}));
			if (series.length === 0) return unsupported(id);
			return {
				id,
				type: "chart",
				variant: raw.variant as IChartBlock["variant"],
				...(isString(raw.title) ? { title: raw.title } : {}),
				series,
				...(raw.unit === "money" ||
				raw.unit === "count" ||
				raw.unit === "percent"
					? { unit: raw.unit }
					: {}),
			};
		}

		case "action": {
			// The confirm card is the money-action safety surface: without a
			// ledger-backed proposalId + idempotencyKey it must NOT render as an
			// approvable card — degrade to UnsupportedBlock instead.
			if (
				!isString(raw.title) ||
				!isString(raw.workflow) ||
				!isString(raw.proposalId) ||
				!isString(raw.idempotencyKey) ||
				!Array.isArray(raw.preview)
			) {
				return unsupported(id);
			}
			const preview = raw.preview.filter(
				(p): p is { label: string; detail: string } =>
					isObject(p) && isString(p.label) && isString(p.detail),
			);
			if (preview.length === 0) return unsupported(id);
			return {
				id,
				type: "action",
				title: raw.title,
				workflow: raw.workflow,
				proposalId: raw.proposalId,
				idempotencyKey: raw.idempotencyKey,
				preview,
				...(isString(raw.summaryAmount)
					? { summaryAmount: raw.summaryAmount }
					: {}),
				...(isString(raw.expiresAt) ? { expiresAt: raw.expiresAt } : {}),
				...(isString(raw.asOf) ? { asOf: raw.asOf } : {}),
			};
		}

		case "citation": {
			if (!Array.isArray(raw.sources)) return unsupported(id);
			const sources = raw.sources.filter(
				(s): s is Record<string, unknown> => isObject(s) && isString(s.label),
			);
			if (sources.length === 0) return unsupported(id);
			// The renderer feeds source.ref.route straight into navigate(), so a
			// drifted/hostile route must be dropped here. A source with an unsafe or
			// absent route keeps its label but renders as a non-clickable chip.
			const safeSources = sources.map((s) => {
				const ref = s.ref;
				if (isObject(ref) && isSafeRoute(ref.route)) {
					return {
						...s,
						ref: {
							route: ref.route,
							...(isObject(ref.filter) ? { filter: ref.filter } : {}),
						},
					};
				}
				return { ...s, ref: undefined };
			});
			return {
				id,
				type: "citation",
				// Labels/kind/count are trusted shallowly; only ref.route is validated
				// above since it is the field that drives navigation.
				sources: safeSources as unknown as ICitationSource[],
			};
		}

		default:
			// A valid-but-not-yet-rendered type (diagram/action/workflow) or an
			// unknown future type: pass through so BlockRenderer's default shows an
			// honest UnsupportedBlock with the real type name.
			return raw as unknown as ChatBlock;
	}
}
